import React, { useState, useRef } from 'react';
import { Camera, X, Upload } from 'lucide-react';

const ProfilePictureUpload = ({ currentPicture, onUpload, onDelete }) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = e.dataTransfer?.files;
    if (files && files[0]) {
      handleFile(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleFile = async (file) => {
    // Validate file type
    if (!file.type.match(/^image\/(jpeg|jpg|png|gif|webp)$/i)) {
      alert('Please select a valid image file (JPEG, PNG, GIF, WebP)');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Please select an image smaller than 5MB');
      return;
    }

    setUploading(true);
    
    try {
      const base64 = await convertToBase64(file);
      const profilePictureData = {
        data: base64.split(',')[1], // Remove data:image/jpeg;base64, prefix
        contentType: file.type
      };
      
      await onUpload(profilePictureData);
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      alert('Failed to upload profile picture. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Resize image to max 800x800 while maintaining aspect ratio
        const MAX_SIZE = 800;
        let { width, height } = img;
        
        if (width > height) {
          if (width > MAX_SIZE) {
            height = (height * MAX_SIZE) / width;
            width = MAX_SIZE;
          }
        } else {
          if (height > MAX_SIZE) {
            width = (width * MAX_SIZE) / height;
            height = MAX_SIZE;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          },
          file.type,
          0.8 // Quality for JPEG compression
        );
      };
      
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to remove your profile picture?')) {
      try {
        await onDelete();
      } catch (error) {
        console.error('Error deleting profile picture:', error);
        alert('Failed to delete profile picture. Please try again.');
      }
    }
  };

  return (
    <div className="relative">
      {/* Upload Button */}
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        className="bg-[#1086a4] hover:bg-[#0c6d85] text-white p-2 rounded-full shadow-lg transition-colors"
        disabled={uploading}
        title="Change profile picture"
      >
        {uploading ? (
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : (
          <Camera className="w-4 h-4" />
        )}
      </button>

      {/* Delete Button - only show if there's a current picture with valid data */}
      {(currentPicture?.url || currentPicture?.data) && (
        <button
          type="button"
          onClick={handleDelete}
          className="absolute -top-1 -right-1 bg-red-500 hover:bg-red-600 text-white p-1 rounded-full shadow-lg transition-colors"
          title="Remove profile picture"
        >
          <X className="w-3 h-3" />
        </button>
      )}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Drag & Drop Overlay - only show when dragging */}
      {dragActive && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <div className="bg-white rounded-lg p-8 text-center">
            <Upload className="w-12 h-12 text-[#1086a4] mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-700">
              Drop your profile picture here
            </p>
            <p className="text-sm text-gray-500 mt-2">
              JPEG, PNG, GIF, WebP (max 5MB)
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePictureUpload;